import { CLIMATE_MONTH_LABELS, HORIZON_COLORS } from "./climateLayer";
import { buildLegend } from "./indicatorEngine";

/**
 * CATALOGUE DES GRAPHIQUES DE TERRITOIRE.
 *
 * CE QUE CE MODULE EST, ET CE QU'IL N'EST PAS. Les etapes de type "graph" d'un
 * module d'introduction affichaient les composants du serious game (radar
 * d'acteurs, score CBS...), pilotes par le `gameStatus` d'une partie. Ce n'est
 * pas de cela qu'on parle ici : ces graphiques-la lisent le DATAPACK du
 * territoire — les memes fichiers que la carte — pour montrer ce que la carte
 * ne sait pas dire (une saisonnalite, une repartition).
 *
 * POURQUOI UN REGISTRE DECLARATIF PLUTOT QUE QUATRE COMPOSANTS. Un graphique
 * est ici trois choses : un fichier a lire, des reglages offerts a l'auteur, et
 * une fonction qui transforme l'un en series affichables. Les trois tiennent
 * dans une entree de ce tableau. L'inspecteur du builder se GENERE a partir de
 * `params` (comme le navigateur de couches se genere a partir des indicateurs
 * du datapack) : ajouter un graphique, c'est ajouter une entree ici, sans
 * ecrire une ligne d'interface. C'est aussi ce qui rend les graphiques
 * editables au sens ou l'entend l'auteur — remplacer les precipitations par
 * l'humidite des sols n'est pas un autre graphique, c'est le meme avec un autre
 * `variable`.
 *
 * FORME NORMALISEE RENDUE PAR `build` (cf. TerritoryChart pour le rendu) :
 *
 *   { form, title, subtitle, unit, labels, series: [{ label, values, color }],
 *     notes: [string], source }
 *
 * ou `{ empty: "…" }` quand le graphique ne peut rien montrer (donnee absente,
 * rien de selectionne sur la carte). Aucune specification ne dessine : elles
 * preparent des nombres, un seul composant les met en image.
 */

// Jetons de cible. Ils vivent dans la meme liste d'options que les cibles
// nommees : « suivre la selection » n'est pas un mode a part avec sa mecanique,
// c'est une valeur possible du meme reglage.
export const SELECTION_TOKEN = "__selection__";
export const TERRITORY_TOKEN = "__territory__";

/**
 * Palette categorielle des repartitions (camemberts, barres d'usages), EN
 * DERNIER RECOURS.
 *
 * Une couleur categorielle nomme une identite ; elle n'a donc rien a faire ici
 * des qu'un VOCABULAIRE existe ailleurs et dit deja de quelle couleur se peint
 * « Vignes » — voir `legendPalette` plus bas. Ce tableau ne sert plus qu'aux
 * modalites que personne ne nomme.
 *
 * SIX TEINTES, PAS DAVANTAGE : au-dela, des classes voisines se confondent.
 * Elle ouvre sur la sarcelle de la charte (--t3d-accent) et suit l'ordre
 * chaud/froid alterne ; les couleurs s'attribuent DANS CET ORDRE, jamais en
 * cycle, pour qu'une part garde sa teinte quand une autre disparait.
 *
 * Elle est calee sur le fond SOMBRE du module (~#12293a) et validee comme un
 * tout : clartes dans la bande sombre (OKLCH L 0,48-0,67), chroma au-dessus du
 * plancher, pire paire adjacente ΔE 10,1 en vision deficiente et 18,9 en
 * vision normale. Deux teintes (corail, violet) tombent a 2,9:1 de contraste
 * sur ce fond : c'est admis parce que chaque part porte son libelle ET sa
 * valeur en clair dans la legende — l'identite ne repose jamais sur la seule
 * couleur. Ne pas y toucher sans repasser le validateur.
 */
const CATEGORICAL = ["#0093b7", "#c14121", "#33ac5a", "#9b6900", "#d16191", "#7b55ca"];

// Teinte des parts regroupees : volontairement hors palette (gris), pour dire
// « ceci n'est pas une categorie, c'est un reste ».
const OTHER_COLOR = "rgba(244, 248, 249, 0.35)";
const OTHER_LABEL = "Autres";

// Part-a-part en un coup d'oeil : au-dela de six secteurs un camembert cesse
// d'etre lisible. Le sixieme secteur est le regroupement.
const MAX_SLICES = 6;

const MONTH_LABELS = CLIMATE_MONTH_LABELS;

// ---------------------------------------------------------------------------
// Utilitaires
// ---------------------------------------------------------------------------

/**
 * COULEURS D'UN VOCABULAIRE, REPRISES DE LA CARTE.
 *
 * Une repartition et une carte posees cote a cote sur le meme ecran parlent du
 * meme monde : la salle lit « le prune, c'est la vigne » sur la carte, puis
 * cherche le prune dans le graphique. Tant que le graphique tirait ses teintes
 * de sa propre palette, DANS L'ORDRE DES PARTS, la correspondance n'existait
 * pas — et une part changeait meme de couleur quand le classement bougeait
 * (selectionner une commune reordonnait les barres, donc les recoloriait).
 *
 * D'ou cette lecture : la couleur d'une modalite est celle que la LEGENDE de
 * l'indicateur lui donne (indicatorEngine.buildLegend), c'est-a-dire la couleur
 * de la source quand le datapack en porte une, et la meme teinte de secours que
 * la carte sinon. Une seule table, au meme endroit que la carte, la legende et
 * la fiche de commune. La couleur devient une propriete de la modalite, pas de
 * son rang.
 *
 * Rend `null` si l'indicateur est absent du datapack — un territoire n'est pas
 * tenu de decrire les memes choses qu'un autre, et le graphique retombe alors
 * sur CATEGORICAL comme avant.
 */
function legendPalette(data, indicatorId) {
  // `findIndicator` retomberait sur le premier indicateur venu : ici, ne pas
  // trouver le vocabulaire demande doit rendre `null`, pas les couleurs d'un
  // autre.
  const spec = (data?.indicators || []).find((item) => item.id === indicatorId);
  if (!spec) return null;
  const entries = buildLegend(spec)?.entries || [];
  if (!entries.length) return null;
  return new Map(entries.map((entry) => [String(entry.value), entry.color]));
}

/**
 * Palette d'un vocabulaire que la carte ne peint pas (les usages de la BNPE).
 * Le principe reste le meme — la couleur suit l'identite, pas le rang — mais
 * l'ordre de reference est ici celui du vocabulaire lui-meme, stable d'une
 * emprise a l'autre, et non celui des parts.
 */
function orderedPalette(labels) {
  return new Map(labels.map((label, index) => [label, CATEGORICAL[index % CATEGORICAL.length]]));
}

export function formatNumber(value, decimals = 1) {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return value.toLocaleString("fr-FR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function pluralize(count, singular, plural) {
  // Espace insecable de groupement : « 1 420 ouvrages », pas « 1420 ».
  return `${count.toLocaleString("fr-FR")} ${count > 1 ? plural : singular}`;
}

/**
 * Couleur d'un horizon climatique. Elle est reprise TELLE QUELLE des fiches de
 * station de la carte (climateLayer) : l'actuel en neutre clair, les horizons
 * futurs de plus en plus chauds. Deux raisons de ne pas prendre la palette
 * categorielle ici — un horizon n'est pas une identite mais un rang sur un axe
 * ordonne, et l'auditoire vient de voir ces memes couleurs sur la carte.
 */
function horizonColor(horizonId, axes) {
  const index = (axes?.horizons || []).findIndex((h) => h.id === horizonId);
  return HORIZON_COLORS[index >= 0 ? index % HORIZON_COLORS.length : 0];
}

function horizonsOf(data) {
  return (data?.axes?.horizons || []).map((h) => ({ value: h.id, label: h.label }));
}

/** Les 12 valeurs mensuelles d'une serie climat, l'annuel etant en tete. */
function monthsOfClimateSeries(series) {
  return Array.isArray(series) ? series.slice(1, 13) : null;
}

/**
 * Regroupe la queue d'une repartition sous « Autres ». Rend les parts triees,
 * plafonnees a MAX_SLICES, et le nombre de postes regroupes — l'affichage le
 * dit, sinon le camembert ment par omission.
 */
function foldSmallSlices(entries, { regroup, maxSlices = MAX_SLICES, threshold = 0.02 }) {
  const sorted = [...entries].sort((a, b) => b.value - a.value);
  const total = sorted.reduce((sum, entry) => sum + entry.value, 0);
  if (!total) return { slices: sorted, folded: 0 };

  const keep = [];
  const fold = [];
  sorted.forEach((entry, index) => {
    const tooSmall = regroup && entry.value / total < threshold;
    const tooMany = index >= maxSlices - 1 && sorted.length > maxSlices;
    if (tooSmall || tooMany) fold.push(entry);
    else keep.push(entry);
  });

  if (!fold.length) return { slices: keep, folded: 0 };
  const foldedValue = fold.reduce((sum, entry) => sum + entry.value, 0);
  return {
    slices: [...keep, { label: OTHER_LABEL, value: foldedValue, color: OTHER_COLOR }],
    folded: fold.length,
  };
}

/** Une repartition -> la forme normalisee d'un camembert ou d'un barres. */
function distributionChart({ entries, form, unit, decimals, title, subtitle, notes, source, regroup, palette }) {
  const { slices, folded } = foldSmallSlices(entries, { regroup });
  if (!slices.length) return { empty: "Aucune donnée à répartir pour cette sélection." };

  const allNotes = [...(notes || [])];
  if (folded) {
    allNotes.push(`${pluralize(folded, "poste regroupé", "postes regroupés")} sous « ${OTHER_LABEL} ».`);
  }

  // Le compteur de secours est TENU A PART, et non indexe sur la part : deux
  // modalites inconnues du vocabulaire doivent recevoir deux teintes, sans
  // qu'une part deja peinte par la legende en consomme une au passage.
  let fallbackIndex = 0;
  const colorOf = (slice) =>
    slice.color                                   // le « reste », hors palette
    || palette?.get(slice.label)                  // le vocabulaire, s'il nomme
    || CATEGORICAL[fallbackIndex++ % CATEGORICAL.length];

  return {
    form,
    title,
    subtitle,
    unit,
    decimals,
    labels: slices.map((slice) => slice.label),
    series: [{
      label: title,
      values: slices.map((slice) => slice.value),
      colors: slices.map(colorOf),
    }],
    notes: allNotes,
    source,
  };
}

// ---------------------------------------------------------------------------
// 1. Debit mensuel d'une station, par horizon climatique
// ---------------------------------------------------------------------------

const dischargeMonthly = {
  id: "discharge_monthly",
  label: "Débit mensuel d'une station",
  hint: "Le régime d'un cours d'eau sur douze mois.",
  dataset: "discharge",
  params: [
    {
      id: "station",
      label: "Station",
      type: "select",
      options: (data) => [
        { value: SELECTION_TOKEN, label: "↳ Station sélectionnée sur la carte" },
        ...(data?.stations || []).map((station) => ({
          value: station.code_station,
          label: `${station.cours_eau}, ${station.lieu}`,
        })),
      ],
      default: (data) => data?.stations?.[0]?.code_station || SELECTION_TOKEN,
    },
    {
      id: "horizons",
      label: "Horizons",
      type: "multi",
      hint: "Deux suffisent à montrer le déplacement.",
      options: horizonsOf,
      default: () => ["actuel", "2050"],
    },
    {
      id: "mode",
      label: "Grandeur",
      type: "select",
      options: () => [
        { value: "debit", label: "Débit (m³/s)" },
        { value: "parde", label: "Coefficients de Pardé (débit ÷ module)" },
      ],
      default: () => "debit",
      hint: "Pardé efface la taille du cours d'eau : deux stations deviennent comparables.",
    },
  ],
  build: ({ data, params, selection }) => {
    const stations = data?.stations || [];
    if (!stations.length) return { empty: "Aucune station de débit dans ce territoire." };

    const notes = [];
    let station = null;
    if (params.station === SELECTION_TOKEN) {
      station = selection?.discharge || null;
      if (!station) {
        station = stations[0];
        notes.push("Aucune station sélectionnée : la première est affichée.");
      }
    } else {
      station = stations.find((s) => s.code_station === params.station) || null;
    }
    if (!station) return { empty: "Station introuvable dans ce jeu de données." };

    const isParde = params.mode === "parde";
    const horizons = (params.horizons || []).filter((id) => station.horizons?.[id]);
    if (!horizons.length) return { empty: "Aucun horizon retenu pour cette station." };

    const series = horizons.map((id) => {
      const horizon = station.horizons[id];
      return {
        label: data.axes?.horizons?.find((h) => h.id === id)?.label || id,
        values: isParde ? horizon.coefficients_parde : horizon.mensuel_m3s,
        color: horizonColor(id, data.axes),
      };
    });

    const reference = station.horizons[horizons[0]];
    return {
      form: "line",
      title: station.cours_eau,
      subtitle: `${station.lieu} · module ${formatNumber(reference.module_m3s, 1)} m³/s`,
      unit: isParde ? "" : "m³/s",
      decimals: isParde ? 2 : 1,
      labels: MONTH_LABELS,
      series,
      notes,
      source: "Explore2, débits reconstitués",
    };
  },
};

// ---------------------------------------------------------------------------
// 2. Repartition des prelevements par usage
// ---------------------------------------------------------------------------

/** Annees renseignees dans withdrawals.json, avec leur couverture en ouvrages. */
function withdrawalYears(data) {
  const counts = new Map();
  (data || []).forEach((point) => {
    (point.ouvrages || []).forEach((ouvrage) => {
      (ouvrage.series || []).forEach((entry) => {
        counts.set(entry.annee, (counts.get(entry.annee) || 0) + 1);
      });
    });
  });
  return [...counts.entries()]
    .map(([annee, ouvrages]) => ({ annee, ouvrages }))
    .sort((a, b) => b.annee - a.annee);
}

/**
 * Annee proposee par defaut. Pas simplement la plus recente : les derniers
 * millesimes sont parfois incomplets, et un camembert bati sur une annee a
 * moitie renseignee raconte n'importe quoi. On prend donc la plus recente
 * parmi celles dont la couverture atteint 90 % de la meilleure annee.
 */
function defaultWithdrawalYear(data) {
  const years = withdrawalYears(data);
  if (!years.length) return null;
  const best = Math.max(...years.map((y) => y.ouvrages));
  return (years.find((y) => y.ouvrages >= best * 0.9) || years[0]).annee;
}

function withdrawalUsages(data) {
  return [...new Set((data || []).map((point) => point.usage))].sort();
}

/**
 * Libelles d'usage de la BNPE, rendus lisibles. Les originaux sont en
 * capitales et vont jusqu'a « INDUSTRIE et ACTIVITES ECONOMIQUES (hors
 * irrigation, hors énergie) » : dans une legende de camembert posee dans une
 * colonne de 470 px, ils ecrasent le dessin. La valeur stockee reste le
 * libelle brut — c'est la cle de regroupement, elle ne se traduit pas.
 */
const USAGE_LABELS = {
  "EAU POTABLE": "Eau potable",
  IRRIGATION: "Irrigation",
  "INDUSTRIE et ACTIVITES ECONOMIQUES (hors irrigation, hors énergie)": "Industrie et activités",
  "EAU TURBINEE (barrage)": "Eau turbinée",
  CANAUX: "Canaux",
  ENERGIE: "Énergie",
};

function usageLabel(usage) {
  if (USAGE_LABELS[usage]) return USAGE_LABELS[usage];
  const lower = usage.toLocaleLowerCase("fr");
  return lower.charAt(0).toLocaleUpperCase("fr") + lower.slice(1);
}

// Les deux usages non consommateurs : l'eau turbinee et l'energie restituent au
// milieu ce qu'elles prelevent, et pesent mille fois les autres. Les laisser
// par defaut ecraserait le camembert en une part unique — ce qui est vrai en
// volume, et faux pour la question posee (qui consomme la ressource).
const NON_CONSUMPTIVE = ["EAU TURBINEE (barrage)", "ENERGIE"];

const withdrawalsByUsage = {
  id: "withdrawals_by_usage",
  label: "Répartition des prélèvements par usage",
  hint: "Les volumes d'une même année, répartis entre usages.",
  dataset: "withdrawals",
  params: [
    {
      id: "annee",
      label: "Année",
      type: "select",
      hint: "Une seule année : deux millésimes ne s'additionnent pas.",
      options: (data) => withdrawalYears(data).map(({ annee, ouvrages }) => ({
        value: annee,
        label: `${annee} : ${pluralize(ouvrages, "ouvrage renseigné", "ouvrages renseignés")}`,
      })),
      default: defaultWithdrawalYear,
    },
    {
      id: "usages",
      label: "Usages retenus",
      type: "multi",
      hint: "Turbinée et énergie écartées : non consommatrices, elles pèsent mille fois les autres.",
      options: (data) => withdrawalUsages(data).map((usage) => ({ value: usage, label: usageLabel(usage) })),
      default: (data) => withdrawalUsages(data).filter((usage) => !NON_CONSUMPTIVE.includes(usage)),
    },
    {
      id: "zone",
      label: "Emprise",
      type: "select",
      options: () => [
        { value: TERRITORY_TOKEN, label: "Territoire entier" },
        { value: SELECTION_TOKEN, label: "↳ Commune sélectionnée sur la carte" },
      ],
      default: () => TERRITORY_TOKEN,
    },
    {
      id: "form",
      label: "Forme",
      type: "select",
      options: () => [
        { value: "pie", label: "Camembert" },
        { value: "bar", label: "Barres" },
      ],
      default: () => "pie",
    },
  ],
  build: ({ data, params, selection }) => {
    const points = Array.isArray(data) ? data : [];
    if (!points.length) return { empty: "Aucun prélèvement recensé dans ce territoire." };

    const year = Number(params.annee);
    if (!year) return { empty: "Choisissez l'année sur laquelle faire la somme." };

    const notes = [];
    let scope = points;
    let zoneLabel = "Territoire entier";
    if (params.zone === SELECTION_TOKEN) {
      const commune = selection?.commune;
      if (!commune) {
        notes.push("Aucune commune sélectionnée : territoire entier.");
      } else {
        zoneLabel = commune.nom_commune;
        scope = points.filter((point) => point.commune === commune.nom_commune);
        if (!scope.length) return { empty: `Aucun prélèvement recensé à ${commune.nom_commune}.` };
      }
    }

    const retained = new Set(params.usages || []);
    const totals = new Map();
    let counted = 0;
    let missing = 0;
    scope.forEach((point) => {
      if (!retained.has(point.usage)) return;
      (point.ouvrages || []).forEach((ouvrage) => {
        const entry = (ouvrage.series || []).find((item) => item.annee === year);
        // Sommer l'annee demandee, et elle seule. Un ouvrage muet cette
        // annee-la n'est pas remplace par son dernier volume connu : ce serait
        // additionner des millesimes differents, ce que ce graphique refuse de
        // faire. Il est signale comme manquant, pas comble.
        if (!entry || entry.volume === null || entry.volume === undefined) { missing += 1; return; }
        counted += 1;
        totals.set(point.usage, (totals.get(point.usage) || 0) + entry.volume);
      });
    });

    if (!totals.size) return { empty: `Aucun volume renseigné en ${year} pour les usages retenus.` };

    // Le million de m3 est l'unite de lecture : en m3 bruts, les etiquettes
    // deviennent des suites de chiffres qu'on ne compare plus.
    const entries = [...totals.entries()].map(([usage, volume]) => ({
      label: usageLabel(usage),
      value: volume / 1e6,
    }));

    notes.push(`Année ${year} seule · ${pluralize(counted, "ouvrage déclaré", "ouvrages déclarés")}.`);
    if (missing) {
      // Dit plutot que masque : sur la BNPE, la moitie des ouvrages recenses
      // peut n'avoir aucune declaration une annee donnee. Un total muet sur ce
      // point se lirait comme une mesure exhaustive du prelevement.
      notes.push(`${pluralize(missing, "autre sans déclaration", "autres sans déclaration")}, non compté${missing > 1 ? "s" : ""}.`);
    }
    const excluded = withdrawalUsages(points).filter((usage) => !retained.has(usage));
    if (excluded.length) notes.push(`Écartés : ${excluded.map(usageLabel).join(", ")}.`);

    return distributionChart({
      entries,
      form: params.form || "pie",
      unit: "Mm³",
      decimals: 1,
      title: `Prélèvements ${year}`,
      subtitle: zoneLabel,
      notes,
      source: "BNPE",
      regroup: false,
      // Le vocabulaire de reference est celui de la BNPE sur CE territoire,
      // pas celui des usages retenus : ecarter l'energie ne doit pas repeindre
      // l'irrigation.
      palette: orderedPalette(withdrawalUsages(points).map(usageLabel)),
    });
  },
};

// ---------------------------------------------------------------------------
// 3. Repartition de la SAU par type de culture
// ---------------------------------------------------------------------------

const CULTURE_FIELD = "agriculture.cultures.postes";

// L'indicateur de carte qui parle le MEME vocabulaire que ce graphique : la
// culture dominante d'une commune est l'un de ces postes. C'est lui qui decide
// de la couleur des parts (cf. `legendPalette`).
const CULTURE_INDICATOR = "agriculture.cultures.culture_dominante";

const sauByCulture = {
  id: "sau_by_culture",
  label: "Répartition de la SAU par culture",
  hint: "La SAU, répartie entre les postes de culture.",
  dataset: "communes",
  params: [
    {
      id: "zone",
      label: "Emprise",
      type: "select",
      options: (data) => [
        { value: TERRITORY_TOKEN, label: "Territoire entier" },
        { value: SELECTION_TOKEN, label: "↳ Commune sélectionnée sur la carte" },
        ...[...(data?.communes || [])]
          .sort((a, b) => a.nom_commune.localeCompare(b.nom_commune, "fr"))
          .map((commune) => ({ value: commune.code_insee, label: commune.nom_commune })),
      ],
      default: () => TERRITORY_TOKEN,
    },
    {
      id: "unit",
      label: "Grandeur",
      type: "select",
      options: () => [
        { value: "ha", label: "Surface (ha)" },
        { value: "pct", label: "Part de la SAU (%)" },
      ],
      default: () => "pct",
    },
    {
      id: "regroup",
      label: "Regrouper les postes marginaux",
      type: "toggle",
      hint: "Les postes sous 2 % passent sous « Autres ».",
      default: () => true,
    },
    {
      id: "form",
      label: "Forme",
      type: "select",
      options: () => [
        { value: "pie", label: "Camembert" },
        { value: "bar", label: "Barres" },
      ],
      default: () => "pie",
    },
  ],
  build: ({ data, params, selection }) => {
    const communes = data?.communes || [];
    if (!communes.length) return { empty: "Aucune commune dans ce jeu de données." };

    const notes = [];
    let scope = communes;
    let zoneLabel = "Territoire entier";
    if (params.zone === SELECTION_TOKEN) {
      const selected = selection?.commune;
      if (!selected) {
        notes.push("Aucune commune sélectionnée : territoire entier.");
      } else {
        scope = [selected];
        zoneLabel = selected.nom_commune;
      }
    } else if (params.zone !== TERRITORY_TOKEN) {
      const found = communes.find((commune) => commune.code_insee === params.zone);
      if (!found) return { empty: "Commune introuvable dans ce jeu de données." };
      scope = [found];
      zoneLabel = found.nom_commune;
    }

    const totals = new Map();
    let secret = 0;
    scope.forEach((commune) => {
      const postes = commune[CULTURE_FIELD];
      // Sous secret statistique, les postes sont listes mais leurs surfaces ne
      // sont pas diffusees (trop peu d'exploitations pour anonymiser). La
      // commune compte alors pour zero, et on le dit.
      if (!Array.isArray(postes) || !postes.length) { secret += 1; return; }
      let contributed = false;
      postes.forEach((poste) => {
        if (poste.surface_ha === null || poste.surface_ha === undefined) return;
        contributed = true;
        totals.set(poste.libelle, (totals.get(poste.libelle) || 0) + poste.surface_ha);
      });
      if (!contributed) secret += 1;
    });

    if (!totals.size) {
      return {
        empty: scope.length === 1
          ? `Surfaces non diffusées pour ${zoneLabel} (secret statistique).`
          : "Aucune surface de culture diffusée sur cette emprise.",
      };
    }

    const sum = [...totals.values()].reduce((total, value) => total + value, 0);
    const asPercent = params.unit === "pct";
    const entries = [...totals.entries()].map(([libelle, surface]) => ({
      label: libelle,
      value: asPercent ? (surface / sum) * 100 : surface,
    }));

    if (secret) {
      notes.push(`${pluralize(secret, "commune sous secret statistique", "communes sous secret statistique")}.`);
    }

    return distributionChart({
      entries,
      form: params.form || "pie",
      unit: asPercent ? "%" : "ha",
      decimals: asPercent ? 1 : 0,
      title: "Surface agricole utile",
      subtitle: `${zoneLabel} · ${formatNumber(sum, 0)} ha`,
      notes,
      source: "Recensement agricole 2010 (Agreste)",
      regroup: params.regroup !== false,
      palette: legendPalette(data, CULTURE_INDICATOR),
    });
  },
};

// ---------------------------------------------------------------------------
// 4. Repartition des specialisations agricoles
// ---------------------------------------------------------------------------

const SPECIALISATION_FIELD = "agriculture.specialisation.libelle";
const SPECIALISATION_INDICATOR = SPECIALISATION_FIELD;
const SAU_FIELD = "agriculture.sau_ha";

/**
 * LE GRAPHIQUE DE LA CARTE PAR DEFAUT.
 *
 * La specialisation agricole est l'indicateur affiche au premier abord
 * (indicators.DEFAULT_COMMUNE_INDICATOR), et le seul dont les couleurs viennent
 * de la source. Il n'avait pourtant aucune repartition a lui : l'ecran montrait
 * une carte de specialisations et, dessous, un graphique de POSTES DE CULTURE —
 * deux nomenclatures et deux millesimes (RA 2020 / RA 2010) dont la salle lit
 * « Viticulture » d'un cote et « Vignes » de l'autre. Aucune regle de couleur ne
 * rattrape cela : il fallait pouvoir mettre en face de la carte une repartition
 * de la MEME variable. Elle herite alors des teintes de la source sans rien
 * declarer, par `legendPalette`.
 */
const specialisationShare = {
  id: "specialisation_share",
  label: "Répartition des spécialisations agricoles",
  hint: "La même variable que la carte par défaut, en parts — mêmes couleurs.",
  dataset: "communes",
  params: [
    {
      id: "mesure",
      label: "Grandeur",
      type: "select",
      hint: "Compter les communes, ou peser chacune de sa surface agricole.",
      options: () => [
        { value: "communes", label: "Nombre de communes" },
        { value: "sau", label: "Surface agricole (ha)" },
      ],
      default: () => "communes",
    },
    {
      id: "regroup",
      label: "Regrouper les spécialisations marginales",
      type: "toggle",
      hint: "Celles sous 2 % passent sous « Autres ».",
      default: () => true,
    },
    {
      id: "form",
      label: "Forme",
      type: "select",
      options: () => [
        { value: "pie", label: "Camembert" },
        { value: "bar", label: "Barres" },
      ],
      default: () => "pie",
    },
  ],
  build: ({ data, params }) => {
    const communes = data?.communes || [];
    if (!communes.length) return { empty: "Aucune commune dans ce jeu de données." };

    const bySau = params.mesure === "sau";
    const totals = new Map();
    let counted = 0;
    let unknown = 0;
    communes.forEach((commune) => {
      const libelle = commune[SPECIALISATION_FIELD];
      // Une commune sans specialisation n'est pas une specialisation de plus :
      // elle est hors du compte, et on le dit. La carte, elle, la peint en gris
      // de non-renseigne (NODATA_COLOR) — meme traitement, deux langages.
      if (!libelle) { unknown += 1; return; }
      const weight = bySau ? commune[SAU_FIELD] : 1;
      if (typeof weight !== "number" || !Number.isFinite(weight)) { unknown += 1; return; }
      counted += 1;
      totals.set(libelle, (totals.get(libelle) || 0) + weight);
    });

    if (!totals.size) return { empty: "Aucune spécialisation renseignée sur ce territoire." };

    const sum = [...totals.values()].reduce((total, value) => total + value, 0);
    const notes = [];
    if (unknown) {
      notes.push(`${pluralize(unknown, "commune sans spécialisation renseignée", "communes sans spécialisation renseignée")}, non comptée${unknown > 1 ? "s" : ""}.`);
    }

    return distributionChart({
      entries: [...totals.entries()].map(([libelle, value]) => ({ label: libelle, value })),
      form: params.form || "pie",
      unit: bySau ? "ha" : "communes",
      decimals: 0,
      title: "Spécialisation agricole",
      subtitle: bySau
        ? `Territoire entier · ${formatNumber(sum, 0)} ha`
        : `Territoire entier · ${pluralize(counted, "commune classée", "communes classées")}`,
      notes,
      source: "Recensement agricole 2020 (Agreste)",
      regroup: params.regroup !== false,
      palette: legendPalette(data, SPECIALISATION_INDICATOR),
    });
  },
};

// ---------------------------------------------------------------------------
// 5. Cycle annuel d'une variable climatique, par horizon
// ---------------------------------------------------------------------------

const climateMonthly = {
  id: "climate_monthly",
  label: "Climat : cycle annuel",
  hint: "Une variable climatique mois par mois.",
  dataset: "climate",
  params: [
    {
      id: "variable",
      label: "Variable",
      type: "select",
      hint: "Pluie, ETP, pluie efficace, infiltration, ruissellement, température, humidité des sols.",
      options: (data) => (data?.axes?.variables || []).map((variable) => ({
        value: variable.id,
        label: variable.label,
      })),
      default: (data) => data?.axes?.variables?.[0]?.id || "precipitation",
    },
    {
      id: "horizons",
      label: "Horizons",
      type: "multi",
      options: horizonsOf,
      default: (data) => (data?.axes?.horizons || []).map((h) => h.id),
    },
    {
      id: "zone",
      label: "Emprise",
      type: "select",
      options: () => [
        { value: TERRITORY_TOKEN, label: "Moyenne du territoire" },
        { value: SELECTION_TOKEN, label: "↳ Point climatique sélectionné sur la carte" },
      ],
      default: () => TERRITORY_TOKEN,
    },
    {
      id: "form",
      label: "Forme",
      type: "select",
      hint: "Les cumuls en barres, les états en courbes.",
      options: () => [
        { value: "auto", label: "Automatique" },
        { value: "bar", label: "Barres" },
        { value: "line", label: "Courbes" },
      ],
      default: () => "auto",
    },
  ],
  build: ({ data, params, selection }) => {
    const points = data?.points || [];
    if (!points.length) return { empty: "Aucun point climatique dans ce territoire." };

    const variable = (data.axes?.variables || []).find((item) => item.id === params.variable);
    if (!variable) return { empty: "Variable climatique introuvable." };

    const notes = [];
    let scope = points;
    let zoneLabel = `Moyenne de ${points.length} points`;
    if (params.zone === SELECTION_TOKEN) {
      const point = selection?.climatePoint;
      if (!point) {
        notes.push("Aucun point sélectionné : moyenne du territoire.");
      } else {
        scope = [point];
        zoneLabel = "Point sélectionné";
      }
    }

    const horizons = (params.horizons || []).filter((id) =>
      scope.some((point) => monthsOfClimateSeries(point.series?.[variable.id]?.[id]))
    );
    if (!horizons.length) return { empty: "Aucun horizon disponible pour cette variable." };

    const series = horizons.map((id) => {
      const sums = new Array(12).fill(0);
      let contributors = 0;
      scope.forEach((point) => {
        const months = monthsOfClimateSeries(point.series?.[variable.id]?.[id]);
        if (!months) return;
        contributors += 1;
        months.forEach((value, index) => { sums[index] += value; });
      });
      return {
        label: data.axes?.horizons?.find((h) => h.id === id)?.label || id,
        values: contributors ? sums.map((total) => total / contributors) : sums,
        color: horizonColor(id, data.axes),
      };
    });

    // Un cumul mensuel (mm) est une quantite tombee dans le mois : des barres
    // le disent. Une temperature ou un indice d'humidite est un etat qui
    // continue d'un mois sur l'autre : une courbe le dit.
    const form = params.form && params.form !== "auto"
      ? params.form
      : (variable.unit === "mm" ? "bar" : "line");

    return {
      form,
      title: variable.label,
      subtitle: zoneLabel,
      unit: variable.unit || "",
      decimals: variable.unit === "mm" ? 0 : 1,
      labels: MONTH_LABELS,
      series,
      notes,
      source: variable.source,
    };
  },
};

// ---------------------------------------------------------------------------

export const CHART_SPECS = [
  dischargeMonthly, withdrawalsByUsage, sauByCulture, specialisationShare, climateMonthly,
];

export function getChartSpec(chartId) {
  return CHART_SPECS.find((spec) => spec.id === chartId) || null;
}

/** Options d'un reglage pour un jeu de donnees donne (l'inspecteur les rend). */
export function paramOptions(param, data) {
  return typeof param.options === "function" ? param.options(data) : [];
}

/**
 * Reglages effectifs : ceux poses par l'auteur, completes par les defauts de la
 * specification. Les defauts dependent de la donnee (les annees disponibles ne
 * se devinent pas), ils ne peuvent donc etre calcules qu'ici, une fois le
 * fichier charge — c'est aussi ce qui permet a un graphique tout juste ajoute,
 * sans aucun reglage, d'afficher quelque chose de sense.
 */
export function resolveParams(spec, data, params) {
  const resolved = { ...(params || {}) };
  (spec?.params || []).forEach((param) => {
    if (resolved[param.id] === undefined || resolved[param.id] === null || resolved[param.id] === "") {
      resolved[param.id] = typeof param.default === "function" ? param.default(data) : undefined;
    }
  });
  return resolved;
}

/** Point d'entree du rendu : specification + donnee + reglages -> forme normalisee. */
export function buildChart(spec, data, params, selection) {
  if (!spec) return { empty: "Graphique inconnu." };
  if (!data) return { empty: "Données du territoire indisponibles." };
  try {
    return spec.build({ data, params: resolveParams(spec, data, params), selection: selection || {} });
  } catch (error) {
    // Un datapack d'un autre territoire peut ne pas porter les memes champs :
    // un graphique qui echoue le dit dans son cadre, il n'emporte pas l'ecran
    // d'atelier avec lui.
    console.error(`chartSpecs: échec de construction de « ${spec.id} »`, error);
    return { empty: "Ce graphique n'a pas pu être construit avec les données de ce territoire." };
  }
}
